import { useEffect, useRef, useState } from 'react';
import { NAME_MAX, cleanName } from '../shared/constants';
import { api } from '../lib/api';
import { toAvatarBlob } from '../lib/image';
import { toast } from '../lib/toast';
import type { SavedPlayer } from '../lib/store';
import { Avatar, Sheet } from './ui';

interface Props {
  player: SavedPlayer | null;
  onClose: () => void;
  onSave: (patch: { name: string; avatarKey: string | null }) => void;
  onForget: () => void;
  /** False when the server has no photo storage. */
  photos: boolean;
  /** Rejects a new name that's already used by a different saved player. */
  nameTaken: (name: string) => boolean;
}

/** Rename a saved player and change or remove their photo. */
export function EditPlayerSheet({ player, onClose, onSave, onForget, photos, nameTaken }: Props) {
  const [name, setName] = useState('');
  const [avatarKey, setAvatarKey] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; key: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const gallery = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);

  // Reset the form whenever a different player is opened.
  useEffect(() => {
    if (!player) return;
    setName(player.name);
    setAvatarKey(player.avatarKey);
    setPreview(null);
  }, [player]);

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview.url)), [preview]);
  const previewUrl = preview && (busy || preview.key === avatarKey) ? preview.url : null;

  if (!player) return null;

  const cleaned = cleanName(name);
  const error = !cleaned ? 'Name is required' : cleaned !== player.name && nameTaken(cleaned) ? 'Name already used' : null;

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await toAvatarBlob(file);
      const url = URL.createObjectURL(blob);
      setPreview({ url, key: null });
      const form = new FormData();
      form.append('file', blob, blob.type === 'image/webp' ? 'avatar.webp' : 'avatar.jpg');
      const { key } = await api<{ key: string }>('/avatars', { method: 'POST', form });
      setPreview({ url, key });
      setAvatarKey(key);
    } catch (err) {
      setPreview(null);
      toast(err instanceof Error ? err.message : 'Could not upload the photo.', 'error');
    } finally {
      setBusy(false);
      if (gallery.current) gallery.current.value = '';
      if (camera.current) camera.current.value = '';
    }
  };

  const save = () => {
    if (error) return;
    onSave({ name: cleaned, avatarKey });
    onClose();
  };

  return (
    <Sheet
      open={Boolean(player)}
      onClose={onClose}
      title="Edit player"
      footer={
        <div className="space-y-2">
          <button type="button" className="btn btn-ink h-12 w-full" disabled={Boolean(error) || busy} onClick={save}>
            Save changes
          </button>
          <button
            type="button"
            className="btn w-full text-sm text-red-800 hover:bg-red-50"
            onClick={() => {
              onForget();
              onClose();
            }}
          >
            Forget this player
          </button>
        </div>
      }
    >
      <div className="flex items-start gap-4">
        <div className="relative">
          <Avatar name={name || player.name} avatarKey={avatarKey} src={previewUrl} size={72} />
          {busy && (
            <span className="absolute inset-0 grid place-items-center rounded-full bg-ink/60 text-white" role="status">
              <span className="animate-spin" aria-hidden>
                ♣
              </span>
              <span className="sr-only">Uploading photo</span>
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <label htmlFor="edit-player-name" className="label">
            Name
          </label>
          <input
            id="edit-player-name"
            dir="auto"
            value={name}
            maxLength={NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (Urdu or English)"
            autoComplete="off"
            aria-invalid={Boolean(error)}
            aria-describedby="edit-player-hint"
            className={`field name text-lg ${error ? 'border-red-600 ring-2 ring-red-600/30' : ''}`}
          />
          <p id="edit-player-hint" className={`mt-1 flex justify-between text-xs ${error ? 'font-semibold text-red-700' : 'text-ink-soft'}`}>
            <span>{error ?? ' '}</span>
            <span className="tabular-nums">
              {name.length}/{NAME_MAX}
            </span>
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {photos && (
          <>
            <button type="button" className="btn btn-outline-ink min-h-10 flex-1 px-3 text-sm" onClick={() => gallery.current?.click()} disabled={busy}>
              🖼 Gallery
            </button>
            <button type="button" className="btn btn-outline-ink min-h-10 flex-1 px-3 text-sm" onClick={() => camera.current?.click()} disabled={busy}>
              📷 Camera
            </button>
          </>
        )}
        {(avatarKey || previewUrl) && (
          <button
            type="button"
            className="btn min-h-10 px-3 text-sm text-red-800 hover:bg-red-50"
            onClick={() => {
              setPreview(null);
              setAvatarKey(null);
            }}
          >
            Remove photo
          </button>
        )}
      </div>
      <input ref={gallery} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} aria-label="Choose a photo" />
      <input ref={camera} type="file" accept="image/*" capture="user" hidden onChange={(e) => onFile(e.target.files?.[0])} aria-label="Take a photo" />
    </Sheet>
  );
}
